<template>
    <transition name="slide-fade" mode="out-in">
        <div class="toast-panel" v-if="shouldShow" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
            <div class="toast-header">
                <v-badge dot :value="hasUnreadNotifications" left color="#ab0658">
                    <span v-if="events.length">{{ events.length > 0 ? activeToast + 1 : 0 }}/{{ events.length }}</span>
                </v-badge>
                <span class="spacer"></span>
                <font-awesome-icon icon="fa-solid fa-times" class="fa-lg close-icon" @click="close" />
            </div>
            <div class="toasts-body">
                <v-carousel
                    v-if="events.length"
                    :cycle="false"
                    v-model="activeToast"
                    height="140"
                    :dark="false"
                    hide-delimiter-background
                    :hide-delimiters="true"
                    :show-arrows="events.length > 1"
                >
                    <v-carousel-item v-for="(toast, i) in events" :key="i">
                        <v-sheet height="100%" elevation="0">
                            <v-row class="fill-height toast show" align="center" justify="center">
                                <div class="thumbs">
                                    <ImageThumbnail
                                        v-for="photo in toast.slides"
                                        class="media"
                                        :src="photo.url"
                                        preset="small"
                                        :type="photo.mediaType"
                                    />
                                </div>
                                <div class="content">
                                    <p class="primary-text m-0 text-truncate" v-if="toast.uploadUserName">
                                        {{ toast.uploadUserName }}
                                    </p>
                                    <p class="secondary-text m-0 text--secondary">
                                        {{ computedToastTitle(toast) }}
                                    </p>
                                    <p class="timestamp-text m-0" :key="timeRefreshValue">
                                        <v-chip class="mr-1" small dark color="#ab0658" v-if="!toast.read">
                                            New
                                        </v-chip>
                                        {{ toast.timestamp | timeAgo }}
                                    </p>
                                </div>
                            </v-row>
                        </v-sheet>
                    </v-carousel-item>
                </v-carousel>
                <div v-else class="empty-notifications">No notifications to display</div>
            </div>
        </div>
    </transition>
</template>

<script>
import ImageThumbnail from '../Media/ImageThumbnail.vue';
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';

const AUTO_CLEAR_TIMEOUT = 15 * 1000;
const AUTO_CLEAR_AFTER_MOUSELEAVE = 3 * 1000;
const AUTO_CLEAR_INTERVAL = 500;

export default {
    components: { ImageThumbnail },
    data() {
        return {
            activeToast: 0,
            timeRefreshValue: 0,
            timeRefreshInterval: null,
            autoClearInterval: null,
            shouldAutoClear: false,
            autoClearTime: 0,
            hasFocus: false,
            // Set this to true to see your own actions in toasts
            // otherwise it will only show events from other users.
            showAllToasts: false,
        };
    },
    props: ['tributeVideo'],
    filters: {
        timeAgo(timestamp) {
            return moment(timestamp).fromNow();
        },
    },
    computed: {
        ...mapGetters('tributeNotifications', {
            events: 'notifications',
            showNotificationsModal: 'showNotificationsModal',
            hasUnreadNotifications: 'hasUnreadNotifications',
        }),
        shouldShow() {
            if (this.showNotificationsModal) {
                if (this.hasUnreadNotifications) {
                    // don't autoClear if the user has mouse over the toasts
                    if (this.hasFocus) {
                        return true;
                    }
                    if (this.shouldAutoClear) {
                        return this.autoClearTime > 0;
                    }
                }
                return true;
            }
            return false;
        },
    },
    methods: {
        handleSocketEvent(type, data) {
            console.log('handleSocketEvent', { data, type });
            const { event, tributeVideo, slides, action, userName } = data;
            this.setIntervals();
            if (event.tributeVideoId === this.tributeVideo.id || event.serviceId === this.tributeVideo.serviceId) {
                this.autoClearTime = AUTO_CLEAR_TIMEOUT;
                this.shouldAutoClear = true;
                switch (type) {
                    case 'NotifyUpload':
                        if (['upload', 'delete', 'remove', 'reorder'].includes(action.toLowerCase())) {
                            if (this.showAllToasts || this.$auth.user.name !== userName) {
                                this.$store.dispatch('tributeNotifications/addNotification', {
                                    event,
                                    action,
                                    slides,
                                    userName,
                                });
                            }
                        }
                        break;
                }
            }
        },
        computedToastTitle(toast) {
            if (toast?.slides?.length) {
                const slidePluralizedLabel = toast.slides.length === 1 ? 'slide' : 'slides';
                switch (toast.action) {
                    case 'Upload':
                        return `Added ${toast.slides.length} ${slidePluralizedLabel}`;
                    case 'Delete':
                        return `Removed ${toast.slides.length} ${slidePluralizedLabel}`;
                    case 'Reorder':
                        return `Reordered ${toast.slides.length} ${slidePluralizedLabel}`;
                }
            }
        },
        close() {
            this.$store.dispatch('tributeNotifications/markAllAsRead');
        },
        setIntervals() {
            if (!this.timeRefreshInterval) {
                this.timeRefreshInterval = setInterval(() => {
                    this.timeRefreshValue++;
                }, 60 * 1000);
            }
            if (!this.autoClearInterval) {
                this.autoClearInterval = setInterval(() => {
                    if (this.shouldAutoClear) {
                        if (!this.hasFocus && this.autoClearTime > 0) {
                            this.autoClearTime -= AUTO_CLEAR_INTERVAL;
                            // console.log('adjusting autoClearTime', this.autoClearTime);
                        }
                        if (this.autoClearTime <= 0) {
                            this.$store.dispatch('tributeNotifications/hide');
                            this.shouldAutoClear = false;
                        }
                    }
                }, AUTO_CLEAR_INTERVAL);
            }
        },
        onMouseEnter() {
            this.hasFocus = true;
        },
        onMouseLeave() {
            this.hasFocus = false;
            this.autoClearTime = AUTO_CLEAR_AFTER_MOUSELEAVE;
            this.shouldAutoClear = true;
        },
    },
    mounted() {
        this.setIntervals();
        this.$root.$on('debugToast', this.handleSocketEvent);
    },
    beforeDestroy() {
        if (this.timeRefreshInterval) {
            clearInterval(this.timeRefreshInterval);
        }
        if (this.autoClearInterval) {
            clearInterval(this.autoClearInterval);
        }
    },
    sockets: {
        NotifyUpload(data) {
            this.handleSocketEvent('NotifyUpload', data);
        },
        NotifyMainPhotoReplace(data) {
            this.handleSocketEvent('NotifyMainPhotoReplace', data);
        },
    },
    watch: {
        events(newVal, oldVal) {
            // console.log(newVal, oldVal);
            // TODO: Should the active notification be updated as new slides come in?
        },
        activeToast(newVal, oldVal) {
            if (newVal !== oldVal) {
                if (!this.events[oldVal].read) {
                    this.$store.dispatch('tributeNotifications/markAsRead', this.events[oldVal]);
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.toast-panel {
    position: absolute;
    top: 80px;
    right: 18px;
    background: #ffffff;
    width: 360px;
    height: 160px;
    border-radius: 8px;
    box-shadow: 0px 3px 10px rgb(0 0 0 / 19%);
    padding: 10px;
    z-index: 11;
    overflow: hidden;

    .toast-header {
        align-items: center;
        justify-content: space-between;
        .spacer {
            flex-grow: 1;
        }
    }
    .toasts-body {
        height: 100%;
        position: relative;
    }
    .empty-notifications {
        display: flex;
        justify-self: center;
        align-items: center;
        position: absolute;
        inset: 0;
        height: 80%;
    }
    .toast {
        display: flex;
        flex-direction: row;
        margin: auto 36px;
        box-shadow: none;
        border: none;
        overflow: hidden;
        gap: 8px;
        align-content: center;
        padding-bottom: 16px;

        .thumbs {
            position: relative;
            width: 76px;
            aspect-ratio: 1;

            .media {
                position: absolute;
                inset: 0;
            }
        }
        .media {
            border-radius: 8px;
        }

        .content {
            display: flex;
            flex-direction: column;
            flex: 1;
            gap: 2px;

            .primary-text {
                font-size: 1rem;
                font-weight: 600;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
                height: 24px;
                color: $brand-magenta;
            }
            .secondary-text {
                font-size: 0.9rem;
                font-weight: 400;
                line-height: 1;
            }
            .timestamp-text {
                font-size: 0.85rem;
                font-weight: 400;
                padding-top: 6px;
            }
        }
    }
}
::v-deep {
    .v-carousel__controls {
        height: 32px;
    }
    button.v-carousel__controls__item.v-btn {
        color: #252424;
    }
    button.v-carousel__controls__item.v-btn.v-item--active.v-btn--active.v-btn--icon.v-btn--round.theme--dark.v-size--small {
        color: $btn-orange;
    }
    // .v-window__prev {
    //     left: -12px;
    // }
    // .v-window__next {
    //     right: -12px;
    // }
    .v-window__prev,
    .v-window__next {
        background: none;
        border-radius: 3px;
        width: 30px;
        margin: 0;
        .theme--dark.v-btn.v-btn--icon {
            color: rgba(0, 0, 0, 0.8);
        }
        .theme--dark.v-btn.v-btn--icon {
            &:hover,
            &:focus {
                color: $btn-orange;

                &:before {
                    background: none;
                }
            }
        }
        .v-ripple__animation {
            display: none !important;
        }
    }
}
.close-icon {
    cursor: pointer;
}
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: opacity 0.8s ease-in-out;
}

.slide-fade-enter,
.slide-fade-leave-to {
    opacity: 0;
}
</style>
